import React, { useEffect } from 'react';
import { Flex, Box, useBreakpointValue, Spinner, Center, Modal, ModalOverlay, ModalContent, ModalBody, VStack, Text, useColorModeValue } from '@chakra-ui/react';
import Sidebar from './components/Sidebar';
import ChatWindow from './components/ChatWindow';
import { useSocket } from '../../../contexts/SocketContext';
import { useChatLogic } from './hooks/useChatLogic';
import { CHAT_EVENTS } from './constants/chatEvents';
import { useGlobal } from '../../../contexts/GlobalContext';
import { useSelector } from 'react-redux';

export default function Chats() {
  const socket = useSocket();
  const { company } = useGlobal();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const user = useSelector((state) => state.user?.profile);
  const isAdmin = user?.isAdmin === true;
  const userRights = user?.userRights?.map(ur => ur.companyId === user?.loginhistory?.companyId);
  const loadingBgColor = useColorModeValue('white', 'navy.800');
  const loadingTextColor = useColorModeValue('brand.500', 'white');

  useEffect(() => {
    if (user && !(isAdmin || userRights?.chat?.view)) {
      window.history.back();
    }
  }, [user]);


  // Use the senior-level logic hook to manage all chat state and socket events
  const {
    conversations,
    messages,
    activeChat,
    setActiveChat,
    sendMessage,
    findItem,
    sendTypingStatus,
    isPartnerTyping,
    loadMore,
    hasMore,
    isLoadingMore,
    handleDeleteMsg,
    isChatListLoading,
    isItemStockLoading
  } = useChatLogic(socket);

  const syncCode = company?.setting?.syncCode;

  // Switch chat conversation
  const handleSelectChat = (chat, activeChatId) => {
    if (socket?.connected) {
      socket.emit(CHAT_EVENTS.LEAVE_CHAT, { chatId: activeChatId });
      console.log("LEAVE_CHAT");
      if (Number(chat?.unreadCount) > 0) {
        socket.emit(CHAT_EVENTS.READ_MSG, { chatId: chat.id });
        console.log("READ_MSG");
      }
    }
    setActiveChat(chat);
  };

  if (isChatListLoading) {
    return (
      <Box h="calc(100vh - 110px)" overflow="hidden" overflowX="hidden" m={0} mt="3.5rem">
        <Center h="full">
          <Spinner size="xl" color="brand.500" thickness="4px" />
        </Center>
      </Box>
    );
  }

  return (
    <Box h="calc(100vh - 110px)" overflow="hidden" overflowX="hidden" m={0} mt="3.5rem">
      {/* Item Stock Creative Loader Modal */}
      <Modal isOpen={isItemStockLoading} onClose={() => {}} isCentered closeOnOverlayClick={false}>
        <ModalOverlay backdropFilter="blur(5px)" bg="blackAlpha.300" />
        <ModalContent bg="transparent" boxShadow="none" maxW="300px">
          <ModalBody display="flex" alignItems="center" justifyContent="center">
            <VStack bg={loadingBgColor} p={6} borderRadius="xl" spacing={4} boxShadow="xl">
              <Spinner size="xl" color="brand.500" thickness="4px" speed="0.8s" />
              <Text fontWeight="600" color={loadingTextColor} fontSize="md">
                Getting item stock...
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Flex h="full" w="full" overflow="hidden">
        {/* Sidebar: Show on desktop, or on mobile only if no active chat */}
        {(!isMobile || !activeChat) && (
          <Sidebar
            conversations={conversations}
            activeChatId={activeChat?.id}
            onSelectChat={handleSelectChat}
            onFindItem={(name) => findItem(name)}
          />
        )}

        {/* Chat Window: Show on desktop, or on mobile only if active chat exists */}
        {(!isMobile || activeChat) && (
          <ChatWindow
            chat={activeChat}
            messages={messages}
            isPartnerTyping={isPartnerTyping}
            onSendMessage={(data) => {
              if (data.type === "TEXT") {
                sendMessage(null, data.text, 'TEXT', { syncCode })
              } else {
                sendMessage(data.url, data.text, data.type, { syncCode })
              }
            }}
            onTyping={sendTypingStatus}
            onBack={() => setActiveChat(null)}
            onLoadMore={loadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onDeleteMsg={handleDeleteMsg}
            showNumber={(isAdmin || userRights?.chat?.showPhone)}
          />
        )}
      </Flex>
    </Box>
  );
}
